import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/auth";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceTypes = [
  "Web Development",
  "SaaS Solutions",
  "AI & Automation",
  "UI / UX Design",
  "Not sure yet",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    project_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We'll reach out within 24 hours.");
      track("contact_submit", {
        service_type: form.project_type || "unspecified",
        has_company: Boolean(form.company),
      });
      setForm({ name: "", email: "", phone: "", company: "", project_type: "", message: "" });
    } catch (err) {
      const detail = formatApiErrorDetail(err?.response?.data?.detail);
      toast.error(detail || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="relative py-28 md:py-40 border-t border-[var(--border-dim)]"
    >
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-2 h-2 bg-[var(--vermilion)] rounded-full" />
          <span className="overline">— Start a project</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-6">
            <h2
              data-testid="contact-headline"
              className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.95]"
            >
              Let's transform<br />
              your ideas into<br />
              <span className="italic font-normal text-[var(--text-2)]">scalable</span>{" "}
              <span className="text-[var(--vermilion)]">digital products.</span>
            </h2>

            <p className="mt-8 text-[var(--text-2)] max-w-md leading-relaxed">
              Free 30-minute consultation. Tell us what you're building and we'll
              come back within 24 hours with a tailored plan.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href="mailto:hello@ksdevarc.com"
                className="group flex items-start gap-4 link-underline w-fit"
                data-testid="contact-info-email"
              >
                <div className="w-10 h-10 border border-[var(--border-dim)] rounded-sm flex items-center justify-center text-[var(--lime)]">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <div className="overline">Email</div>
                  <div className="font-display text-base mt-1">hello@ksdevarc.com</div>
                </div>
              </a>
              <div className="flex items-start gap-4" data-testid="contact-info-location">
                <div className="w-10 h-10 border border-[var(--border-dim)] rounded-sm flex items-center justify-center text-[var(--lime)]">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <div className="overline">Studio</div>
                  <div className="font-display text-base mt-1">Remote · Worldwide</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border border-[var(--border-dim)] bg-[var(--bg-2)] p-6 md:p-10 rounded-sm relative overflow-hidden"
            >
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-[var(--vermilion)] opacity-10 blur-3xl pointer-events-none" />

              <div className="relative space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field
                    id="contact-input-name"
                    label="Name"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Cooper"
                  />
                  <Field
                    id="contact-input-email"
                    label="Email Address"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@company.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field
                    id="contact-input-phone"
                    label="Contact Number"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+62 812 3456 7890"
                  />
                  <Field
                    id="contact-input-company"
                    label="Company Name"
                    optional
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Acme Co."
                  />
                </div>

                <div>
                  <label className="overline block mb-3">Service Type</label>
                  <div className="flex flex-wrap gap-2">
                    {serviceTypes.map((p) => (
                      <button
                        key={p}
                        type="button"
                        data-testid={`contact-service-type-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                        onClick={() => setForm((s) => ({ ...s, project_type: p }))}
                        className={`font-mono-ui text-[10px] tracking-[0.2em] uppercase px-3 py-2 rounded-full border transition-colors ${
                          form.project_type === p
                            ? "bg-[var(--lime)] text-black border-[var(--lime)]"
                            : "border-[var(--border-dim)] text-[var(--text-2)] hover:text-[var(--text-1)] hover:border-[var(--text-3)]"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-input-message" className="overline block mb-2">
                    Message <span className="text-[var(--vermilion)]">*</span>
                  </label>
                  <textarea
                    id="contact-input-message"
                    data-testid="contact-input-message"
                    required
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder="A short description of what you're trying to build..."
                    className="w-full bg-[var(--bg)]/40 border border-[var(--border-dim)] rounded-sm px-4 py-3 font-mono-ui text-sm text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:border-[var(--vermilion)] transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={loading}
                  className="group w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-[var(--vermilion)] hover:bg-[var(--vermilion-2)] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium tracking-wide rounded-full transition-all duration-300"
                >
                  {loading ? "Submitting..." : "Submit"}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                </button>

                <p className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-3)] text-center">
                  We respond within 24h · No spam, ever
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, type = "text", value, onChange, placeholder, required = false, optional = false }) {
  return (
    <div>
      <label className="overline block mb-2" htmlFor={id}>
        {label}{" "}
        {required && <span className="text-[var(--vermilion)]">*</span>}
        {optional && <span className="text-[var(--text-3)] normal-case tracking-normal">(Optional)</span>}
      </label>
      <input
        id={id}
        data-testid={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full bg-[var(--bg)]/40 border border-[var(--border-dim)] rounded-sm px-4 py-3 font-mono-ui text-sm text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:border-[var(--vermilion)] transition-colors"
      />
    </div>
  );
}
